'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSlide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  alt: string;
  cta: {
    primary: {text: string;href: string;};
    secondary: {text: string;href: string;};
  };
}

const HeroSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroSlides: HeroSlide[] = [
  {
    id: 1,
    title: "Powering Africa's Mining Operations",
    subtitle: "Premium Logistics for Extractive Industries",
    description: "Specialized transport solutions for minerals, heavy equipment, and personnel across East, Central, and Southern Africa with real-time tracking and full compliance.",
    image: "https://images.unsplash.com/photo-1667141053507-ccc499738017",
    alt: "Large yellow mining dump truck carrying minerals at open-pit mine site under blue sky",
    cta: {
      primary: { text: "Request Quote", href: "/contact-and-quotes" },
      secondary: { text: "View Services", href: "/services-overview" }
    }
  },
  {
    id: 2,
    title: "Cross-Border Excellence",
    subtitle: "Seamless Regional Connectivity",
    description: "Navigate complex customs procedures across DRC, Kenya, Tanzania, Uganda, Rwanda, and Burundi with our expert brokerage and security services.",
    image: "https://images.unsplash.com/photo-1728503151088-c520cba8e7c8",
    alt: "Red cargo container truck driving on highway through green African landscape",
    cta: {
      primary: { text: "Explore Coverage", href: "/regional-coverage" },
      secondary: { text: "Track Shipment", href: "/contact-and-quotes" }
    }
  },
  {
    id: 3,
    title: "Safety-First Operations",
    subtitle: "Zero-Compromise Standards",
    description: "Industry-leading safety protocols, environmental stewardship, and comprehensive insurance coverage protecting your valuable cargo every mile.",
    image: "https://images.unsplash.com/photo-1607129274461-9496995b4113",
    alt: "Professional logistics worker in orange safety vest and hard hat inspecting cargo shipment",
    cta: {
      primary: { text: "Safety Records", href: "/safety-and-compliance" },
      secondary: { text: "Learn More", href: "/about" }
    }
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isHydrated, heroSlides.length]);

  const handleSlideChange = (index: number) => {
    if (!isHydrated) return;
    setCurrentSlide(index);
  };

  const handlePrevSlide = () => {
    if (!isHydrated) return;
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const handleNextSlide = () => {
    if (!isHydrated) return;
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  return (
    <section className="relative h-[600px] lg:h-[700px] overflow-hidden bg-gray-900">
      {/* Slides */}
      {heroSlides.map((slide, index) =>
      <div
        key={slide.id}
        className={`absolute inset-0 transition-opacity duration-1000 ${
        index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`
        }>

          <div className="absolute inset-0">
            <AppImage
            src={slide.image}
            alt={slide.alt}
            className="w-full h-full object-cover" />

            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/70 to-transparent" />
          </div>

          <div className="relative z-20 container mx-auto px-4 h-full flex items-center">
            <div className="max-w-2xl">
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark rounded-full mb-6">
                <span className="font-cta font-semibold text-sm text-primary">
                  {slide.subtitle}
                </span>
              </div>
              
              <h1 className="font-headline font-bold text-4xl lg:text-6xl text-white mb-6 leading-tight">
                {slide.title}
              </h1>
              
              <p className="font-body text-lg lg:text-xl text-gray-200 mb-8 leading-relaxed">
                {slide.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                href={slide.cta.primary.href}
                className="inline-flex items-center justify-center px-8 py-4 font-cta font-semibold text-base text-primary bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark rounded-lg shadow-golden-md hover:shadow-golden-lg transition-all duration-300 hover:-translate-y-1">

                  <span>{slide.cta.primary.text}</span>
                  <Icon name="ArrowRightIcon" size={20} className="ml-2" />
                </Link>
                
                <Link
                href={slide.cta.secondary.href}
                className="inline-flex items-center justify-center px-8 py-4 font-cta font-semibold text-base text-white bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-lg hover:bg-white/20 transition-all duration-300">

                  <span>{slide.cta.secondary.text}</span>
                  <Icon name="ChevronRightIcon" size={20} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Controls */}
      <div className="absolute bottom-8 left-0 right-0 z-30">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Slide Indicators */}
            <div className="flex items-center space-x-3">
              {heroSlides.map((_, index) =>
              <button
                key={index}
                onClick={() => handleSlideChange(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ?
                'w-12 bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark' : 'w-2 bg-white/40 hover:bg-white/60'}`
                }
                aria-label={`Go to slide ${index + 1}`} />

              )}
            </div>

            {/* Arrow Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevSlide}
                className="w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/30 rounded-full hover:bg-white/20 transition-all duration-300"
                aria-label="Previous slide">

                <Icon name="ChevronLeftIcon" size={24} className="text-white" />
              </button>
              <button
                onClick={handleNextSlide}
                className="w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/30 rounded-full hover:bg-white/20 transition-all duration-300"
                aria-label="Next slide">

                <Icon name="ChevronRightIcon" size={24} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;